import { useState, useEffect, forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Select,  Box, Paper, Text, Group, Divider, Grid,Center} from "@mantine/core";
import { useForm } from "@mantine/form";
import { Row, Col, Stack } from "react-bootstrap";
import {
  GetCarreras,
  GetCampusDisp,
  GetTurnosDisp,
  GetValor,
  ClearValor,
  GetAnioCarrera
} from "../actions/CarreraActions";
import { SaveResponses, clearCheckoutSummary } from "../actions/ResponsesActions";
import useAvailableDates, { getDefaultFechaIngreso, getPeriodoFromFecha } from "../hooks/useAvailableDates";
import useFormTracking from "../hooks/useFormTracking";
import parsePriceNumber from "../utils/parsePriceNumber";
import typeConfig from "../config/typeConfig";
import { tipoBonifComunidad } from "../config/bonificacionComunidad";
import { IconCalendar, IconId } from '@tabler/icons-react';
import { Skeleton } from "@mantine/core";
import SadFaceGif from '../assets/sad_face_gif.gif'

const toTitleCase = (str) =>
  str
    ? str.toLowerCase().split(' ').map((w) =>
        w.length > 3 ? w.charAt(0).toUpperCase() + w.slice(1) : w
      ).join(' ')
    : str;

// Localizaciones: "091 - UAI - CENTRO" → "Centro". El id 120 es UAI Regional Rosario y el resto
// (090 a 099) son de Buenos Aires; los alumnos de Rosario elegian Centro por error, asi que el
// combo las agrupa por region. Si la lista no trae Rosario (otras entidades) queda plana.
const CAMPUS_ROSARIO_ID = '120';
const campusLabel = (descripcion) =>
  toTitleCase(
    (descripcion || '')
      .replace(/^\s*\d+\s*-\s*/, '')
      .replace(/^UAI\s*-\s*/i, '')
      .trim()
  ) || descripcion;
const buildCampusOptions = (campusList) => {
  if (!campusList) return campusList;
  const toOption = (c) => ({ value: c.c_campus_id, label: campusLabel(c.vc_descripcion) });
  const esRosario = (c) => (c.c_campus_id || '').trim() === CAMPUS_ROSARIO_ID;
  if (!campusList.some(esRosario)) return campusList.map(toOption);
  return [
    { group: 'Buenos Aires', items: campusList.filter((c) => !esRosario(c)).map(toOption) },
    { group: 'Rosario', items: campusList.filter(esRosario).map(toOption) },
  ];
};

/** Separa carrera-plan: "M7-10" → { cod: "M7", plan: "10" }. */
const parseCarreraCodPlan = (carreraValue) => {
  if (!carreraValue) return { cod: null, plan: null };
  const idx = String(carreraValue).indexOf('-');
  if (idx === -1) return { cod: carreraValue, plan: null };
  return {
    cod: carreraValue.slice(0, idx),
    plan: carreraValue.slice(idx + 1),
  };
};

const buildCarreraFromQuery = (c, p) => {
  if (!c || !p) return null;
  return `${String(c).trim()}-${String(p).trim()}`.toUpperCase();
};

const EducationalProgramForm = forwardRef(({ stepNumber, queryParams, id }, ref) => {
  const dispatch = useDispatch();
  
  const response = useSelector( (state) => state.respuestas.steps[stepNumber]?.responses || {});
  const knownUser = useSelector((state) => state.respuestas.knownUser);
  const summary = useSelector((state) => state.respuestas.checkoutSummary);
  // Datos resueltos desde el link cifrado "l" (linksEventos). Sin link → undefined y todo se
  // comporta exactamente como siempre. El link fija tipo de asistente + localización + turno
  // (las listas de precios de SIGUE se configuran por campus/turno).
  const linkEventoData = useSelector((state) => state.respuestas.linkEvento?.data);
  const tipoAsistenteLink = linkEventoData?.tipoAsistenteCodigo;
  
  const {data: dataCarreras ,loading: loadingCarreras} = useSelector(state => state.carreras.carreras);
  const {data:dataCampus,loading:loadingCampus} = useSelector(state => state.carreras.campus);
  const {data:dataTurnos,loading:loadingTurnos, error: errorTurnos} = useSelector(state => state.carreras.turnos);
  const dataAnioCarrera = useSelector(state => state.carreras.anioCarrera.data);
  const valorNormal = useSelector(state => state.carreras.valor.normal);

  const { dates: fechas, loading: loadingFechas } = useAvailableDates(queryParams.fi, queryParams.t);
  const { track } = useFormTracking(stepNumber);

  const MODALIDAD_COLORS = {
    'Presencial':     '#881230',
    'Virtual':        '#245A98',
    'Semipresencial': '#93278F',
  };

  const carreraModalidadMap = useMemo(() => {
    const map = new Map();
    dataCarreras?.forEach((facultad) =>
      facultad.careers?.forEach((c) => map.set(c.cod, c.modalidad || null))
    );
    return map;
  }, [dataCarreras]);
  // Todo cambio en la cascada (fecha, carrera, sede, año, turno) invalida el precio: se limpian
  // los slots de valor y el resumen juntos, asi la tarjeta nunca muestra un precio de otra seleccion.
  const resetPrecio = () => {
    dispatch(ClearValor());
    dispatch(clearCheckoutSummary());
  };
  const urlQueryPrefillDone = useRef(false);
  const anos = useMemo(() => {
    if (dataAnioCarrera?.[0]?.value) {
      return Array.from({ length: parseInt(dataAnioCarrera[0].value, 10) }, (_, i) => ({
        value: (i + 1).toString(),
        label: (i + 1).toString(),
      }));
    }
    return [];
  }, [dataAnioCarrera]);

  const form = useForm({
    initialValues: {
      fechaIngreso: response.fechaIngreso || null,
      carrera: response.carrera || null,
      campus: response.campus || null,
      anioCursada: response.anioCursada || null,
      turno: response.turno || null,
    },
    validate: {
      fechaIngreso: (value) => (value ? null : "El campo es obligatorio"),
      carrera: (value) => (value ? null : "La carrera es obligatoria"),
      campus: (value) => (value ? null : "El campus es obligatorio"),
      anioCursada: (value) => (value ? null : "El año es obligatorio"),
      turno: (value) => (value ? null : "El turno es obligatorio"),
    },
  });

  useEffect(() => {
    if (!response) return;
    form.setValues((prevValues) => ({
      ...prevValues,
      fechaIngreso: response.fechaIngreso ?? prevValues.fechaIngreso,
      carrera: response.carrera ?? prevValues.carrera,
      campus: response.campus ?? prevValues.campus,
      anioCursada: response.anioCursada ?? prevValues.anioCursada,
      turno: response.turno ?? prevValues.turno,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo re-sincronizar cuando cambia el step en redux
  }, [response.fechaIngreso, response.carrera, response.campus, response.anioCursada, response.turno]);

  // Al montar: si hay carrera/sede/año/turno guardados (sessionStorage) pero sus catalogos no
  // estan cargados (no se persisten: store.js blacklist 'carreras'), se descartan junto con el
  // precio. Sin la lista, el valor guardado no se puede mostrar ni validar, y el resumen guardado
  // podria no corresponder a lo que hay en pantalla. Al volver atras dentro de la misma sesion los
  // catalogos siguen en memoria y no se toca nada. El periodo se conserva siempre.
  // Excepcion: con ?c=&p= la carrera viene fija (combo deshabilitado) y no se puede re-elegir; se
  // conserva y se vuelven a pedir sus sedes y años para que la cascada pueda continuar.
  const staleSelectionChecked = useRef(false);
  useEffect(() => {
    if (staleSelectionChecked.current) return;
    staleSelectionChecked.current = true;

    const carreraUrl = buildCarreraFromQuery(queryParams.c, queryParams.p);   // null si no viene ?c=&p=
    const sedesCargadas = (dataCampus?.length ?? 0) > 0;
    const carrerasCargadas = (dataCarreras?.length ?? 0) > 0;

    // Con carrera fija por URL, la URL manda: una carrera guardada distinta (otro link en la misma
    // pestaña) es tan invalida como una sede sin catalogo.
    const carreraGuardadaDistinta = Boolean(carreraUrl && response.carrera && response.carrera !== carreraUrl);
    const descartarCascada =
      ((response.campus || response.anioCursada || response.turno) && !sedesCargadas) || carreraGuardadaDistinta;
    const descartarCarrera = response.carrera && !carrerasCargadas && !carreraUrl;

    if (descartarCascada || descartarCarrera) {
      const carrera = descartarCarrera ? null : (carreraGuardadaDistinta ? carreraUrl : undefined);
      form.setValues({
        ...(carrera !== undefined ? { carrera } : {}),
        campus: null,
        anioCursada: null,
        turno: null,
      });
      resetPrecio();
      dispatch(
        SaveResponses(id, {
          ...(carrera !== undefined ? { carrera } : {}),
          campus: null,
          anioCursada: null,
          turno: null,
          valorCarrera: null,
        })
      );
    }

    // Carrera fija por URL con el catalogo de sedes vacio (recarga, u otro link en la misma
    // pestaña): pedir sedes y años DE LA CARRERA DE LA URL, con la misma fecha que muestra el
    // periodo. Va fuera del descarte: tambien hace falta cuando no habia nada que descartar. El
    // efecto de pre-carga no lo hace porque, con carrera guardada, se retira.
    if (carreraUrl && response.carrera && !sedesCargadas) {
      const fecha = getDefaultFechaIngreso(queryParams.fi, queryParams.t, response.fechaIngreso);
      const { cod, plan } = parseCarreraCodPlan(carreraUrl);
      if (fecha && cod && plan) {
        const [year] = fecha.split('-');
        dispatch(GetCampusDisp(year, cod, plan, getPeriodoFromFecha(fecha), queryParams.e));
        dispatch(GetAnioCarrera(cod, plan, queryParams.e));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al montar
  }, []);

  // Pre-carga, una sola vez por montaje. Las carreras del periodo inicial ya las pidio CheckoutPage
  // en la fase de carga (CargarCarrerasIniciales), con la MISMA fecha que se calcula aca
  // (getDefaultFechaIngreso); este efecto solo deja ese periodo seleccionado si estaba vacio y,
  // con ?c=&p=, preselecciona la carrera y pide sus sedes y años. No usar handleSelectFecha
  // (borra carrera antes de sedes). El resto de los pasos se habilita en cascada como siempre.
  useEffect(() => {
    if (urlQueryPrefillDone.current) return;
    if (loadingFechas || fechas.length === 0) return;

    const defaultFecha = getDefaultFechaIngreso(queryParams.fi, queryParams.t, response.fechaIngreso);
    if (!defaultFecha) return;

    if (!queryParams.c || !queryParams.p) {
      urlQueryPrefillDone.current = true;
      if (!response.fechaIngreso) {
        form.setFieldValue('fechaIngreso', defaultFecha);
        dispatch(SaveResponses(id, { fechaIngreso: defaultFecha }));
      }
      return;
    }

    // El guard por useRef se reinicia en cada montaje, y este paso se desmonta al avanzar. Si ya hay una carrera guardada en redux, la seleccion del usuario manda.
    if (response.carrera) {
      urlQueryPrefillDone.current = true;
      return;
    }

    const carreraCod = buildCarreraFromQuery(queryParams.c, queryParams.p);
    if (!carreraCod) return;

    urlQueryPrefillDone.current = true;

    const periodo = getPeriodoFromFecha(defaultFecha);
    const [year] = defaultFecha.split('-');
    const { cod, plan } = parseCarreraCodPlan(carreraCod);

    form.setValues({
      fechaIngreso: defaultFecha,
      carrera: carreraCod,
      campus: null,
      anioCursada: null,
      turno: null,
    });

    if (cod && plan) {
      dispatch(GetCampusDisp(year, cod, plan, periodo, queryParams.e));
      dispatch(GetAnioCarrera(cod, plan, queryParams.e));
    }

    dispatch(
      SaveResponses(id, {
        fechaIngreso: defaultFecha,
        carrera: carreraCod,
        campus: null,
        anioCursada: null,
        turno: null,
        valorCarrera: null,
      })
    );
  }, [loadingFechas, fechas, queryParams.fi, queryParams.c, queryParams.p, queryParams.t, queryParams.e, dispatch, id]);


  useEffect(() => {
    if (dataCampus?.length === 1 && !form.values.campus) {
      const campus = dataCampus[0];
      handleSelectCampus(campus.c_campus_id);
    }
    if (queryParams.t === "m"&& !form.values.campus) {
      handleSelectCampus('4000');
    }
  }, [form.values.carrera], dataCampus);

  useEffect(() => {
    if (anos?.length === 1 && !form.values.anioCursada) {
      const anio = anos[0].value;
      handleSelectAno(anio);
    }
  }, [form.values.campus,anos]);

  useEffect(() => {
    if (dataTurnos?.length === 1 && !form.values.turno) {
      const turno = dataTurnos[0];
      handleSelectTurno(turno.c_turno_id);
    }
  }, [form.values.anioCursada, dataTurnos]);

  // linksEventos: la localización viene FIJADA en el link — se precarga cuando las opciones están
  // disponibles, reusando la cascada existente (handleSelectCampus dispara año/turnos igual que un
  // click). Se usa el value EXACTO de la opción (los ids de SIGUE pueden traer espacios de padding).
  // Si el campus del link ya no existe para el curso, no se fuerza nada: el usuario elige manual.
  useEffect(() => {
    const linkCampus = linkEventoData?.campusId?.trim();
    if (!linkCampus || form.values.campus) return;
    const match = dataCampus?.find((c) => (c.c_campus_id || '').trim() === linkCampus);
    if (match) handleSelectCampus(match.c_campus_id);
  }, [dataCampus, linkEventoData]);

  // linksEventos: idem para el turno fijado en el link.
  useEffect(() => {
    const linkTurno = linkEventoData?.turnoId?.trim();
    if (!linkTurno || form.values.turno) return;
    const match = dataTurnos?.find((t) => (t.c_turno_id || '').trim() === linkTurno);
    if (match) handleSelectTurno(match.c_turno_id);
  }, [dataTurnos, linkEventoData]);

  useEffect(() => {
    if(queryParams.em){
      dispatch(SaveResponses(id, { ...form.values, mailAcesor:queryParams.em }));
    }
  }, [queryParams]);

  useImperativeHandle(ref, () => ({
    validate: () => {
      const validation = form.validate();
      if (validation.hasErrors) {
        Object.keys(validation.errors).forEach((field) =>
          track('field_error', { fieldName: field })
        );
        return false;
      }
      return true;
    },
  }));

  // Helpers para comparar precios evitando diferencias de redondeo
  const toNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    const normalized = String(value).replace(/\./g, '').replace(',', '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? 0 : parsed;
  };
  const areEqual = (a, b) => Math.abs(toNumber(a) - toNumber(b)) < 0.005;

  const lockedCarreraFromQuery = useMemo(
    () => buildCarreraFromQuery(queryParams.c, queryParams.p),
    [queryParams.c, queryParams.p]
  );

  const handleSelectFecha = (value) => {
    resetPrecio();
    const periodo = getPeriodoFromFecha(value);
    const [year] = (value || '').split('-');

    // Con ?c=&p= la carrera queda fija: al cambiar fecha hay que recargar sedes, no borrar carrera
    if (lockedCarreraFromQuery) {
      form.setValues({
        fechaIngreso: value,
        carrera: lockedCarreraFromQuery,
        campus: null,
        anioCursada: null,
        turno: null,
        valorCarrera: null,
      });

      dispatch(GetCarreras(value, periodo, queryParams.t, queryParams.e));

      const { cod, plan } = parseCarreraCodPlan(lockedCarreraFromQuery);
      if (cod && plan) {
        dispatch(GetCampusDisp(year, cod, plan, periodo, queryParams.e));
        dispatch(GetAnioCarrera(cod, plan, queryParams.e));
      }

      dispatch(
        SaveResponses(id, {
          fechaIngreso: value,
          carrera: lockedCarreraFromQuery,
          campus: null,
          anioCursada: null,
          turno: null,
          valorCarrera: null,
        })
      );
      return;
    }

    form.setValues({ fechaIngreso: value, carrera: null, campus: null, anioCursada: null, turno: null, valorCarrera: null });

    dispatch(GetCarreras(value, periodo, queryParams.t, queryParams.e));
    dispatch(SaveResponses(id, { fechaIngreso: value, carrera: null, campus: null, anioCursada: null, turno: null, valorCarrera: null }));
    if (value) track('field_completed', { fieldName: 'fechaIngreso' });
  };
  
  const handleSelectCarrera = (fechaIngreso, value) => {
    resetPrecio();
    if (!fechaIngreso) {
      console.warn("Fecha de ingreso no seleccionada");
      return;
    }

    const [year] = fechaIngreso.split('-');
    const periodo = getPeriodoFromFecha(fechaIngreso);

    form.setValues({ carrera: value, campus: null, anioCursada: null, turno: null, valorCarrera: null });

    if (value) {
      const { cod, plan } = parseCarreraCodPlan(value);
      if (cod && plan) {
        dispatch(GetCampusDisp(year, cod, plan, periodo, queryParams.e));
        dispatch(GetAnioCarrera(cod, plan, queryParams.e));
      }
    }

    dispatch(SaveResponses(id, { fechaIngreso, carrera: value, campus: null, anioCursada: null, turno: null, valorCarrera: null }));
    if (value) track('field_completed', { fieldName: 'carrera' });
  };



  const handleSelectCampus = (value) => {
    resetPrecio();
    form.setValues({campus:value, anioCursada: null, turno: null ,valorCarrera: null});
    dispatch(SaveResponses(id, { ...form.values, campus: value, anioCursada: null, turno: null, valorCarrera: null }));
    if (value) track('field_completed', { fieldName: 'campus' });
  };

  const handleSelectAno = (value) => {
    resetPrecio();
    form.setValues({anioCursada: value, turno: null,valorCarrera: null });
    if(form.values.carrera){
      const { cod, plan } = parseCarreraCodPlan(form.values.carrera);
      const [year] = form.values.fechaIngreso.split('-');
      const periodo = getPeriodoFromFecha(form.values.fechaIngreso);

      if (cod && plan && year && form.values.campus) {
        dispatch(GetTurnosDisp(year, cod, plan, value, form.values.campus, periodo, queryParams.e));
      } else {
        console.warn("Valores incompletos para GetTurnosDisp:", {
          cod,
          plan,
          year,
          campus: form.values.campus,
          anioCursada: value,
        });
    }
   
    }
    dispatch(SaveResponses(id, { ...form.values, anioCursada: value, turno: null, valorCarrera: null }));
    if (value) track('field_completed', { fieldName: 'anioCursada' });
  };

 
  

  const handleSelectTurno = (value) => {
    resetPrecio();
    form.setValues({turno: value});
    if(form.values.carrera){
      const { cod: codCarrera, plan } = parseCarreraCodPlan(form.values.carrera);
      const ano = form.values.fechaIngreso?.slice(0, 4);
      
      // Si vino un link cifrado de linksEventos, el tipo de asistente lo define el link;
      // si no, "externo" como siempre.
      const tipoAsistente = tipoAsistenteLink || "externo";

      // Estrategia de dos llamadas API para miembros UAI (la segunda solo si el
      // tipo de programa tiene bonificacion de comunidad configurada en SIGUE)
      const bonifComunidad = tipoBonifComunidad(queryParams.t);
      if (knownUser.comunity && bonifComunidad) {
        // Primera llamada: tipoBonificacion = null (precio publico vigente)
        dispatch(GetValor(ano, codCarrera, plan, form.values.anioCursada, form.values.campus, value, "EFECTIVO",form.values.fechaIngreso,tipoAsistente, null, queryParams.e));

        //Segundo llamado para miembros UAI
        setTimeout(() => {
          dispatch(GetValor(ano, codCarrera, plan, form.values.anioCursada, form.values.campus, value, "EFECTIVO",form.values.fechaIngreso,tipoAsistente, bonifComunidad, queryParams.e));
        }, 1000);
      } else {
        // Una sola llamada para usuarios no UAI
        dispatch(GetValor(ano, codCarrera, plan, form.values.anioCursada, form.values.campus, value, "EFECTIVO",form.values.fechaIngreso,tipoAsistente, null, queryParams.e));
      }
      
      dispatch(SaveResponses(id, { ...form.values, turno: value }));
      if (value) track('field_completed', { fieldName: 'turno' });
    }
  };

  const seleccionCompleta = dataCarreras?.length>0 && response.fechaIngreso && response.carrera && 
  response.campus && response.anioCursada && 
  (queryParams.t ==='m' ? true : response.turno);
  // Micro sin cupos: la tarjeta muestra el aviso, no hay precio que esperar.
  const sinCuposMicro = queryParams.t === 'm' && (dataTurnos?.length === 0 || errorTurnos !== null);

  // Sin turnos con cupo para la sede/año elegidos (grado, posgrado y cursos; micro ya tiene su tarjeta).
  // Antes el select quedaba vacío con "No se encontraron resultados" y nada decía qué hacer: en el
  // tracking se veía como "eligió sede y se fue" (análisis 29/09/2026). Solo después de haber consultado.
  const turnosConsultados = useRef(false);
  if (loadingTurnos) turnosConsultados.current = true;
  const sinTurnos = queryParams.t !== 'm' && !!form.values.anioCursada && turnosConsultados.current
    && !loadingTurnos && !linkEventoData?.turnoId
    && (errorTurnos !== null || dataTurnos?.length === 0);

  // getCoste respondió con error, sin filas, o con una fila de precio 0 (lista de precios no configurada
  // para esa combinación; el resumen la rechaza con summary.error): antes la tarjeta de precio
  // simplemente no aparecía y la persona no sabía si esperar.
  const precioRespondido = !!valorNormal.cargado && !valorNormal.loading;
  const precioNoDisponible = seleccionCompleta && !sinCuposMicro && precioRespondido
    && (valorNormal.error != null || !(valorNormal.data?.length > 0) || summary.error != null);

  // La tarjeta se muestra con el precio ya cargado; antes aparecia al elegir turno con el resumen
  // vacio o el anterior, y el precio "saltaba" cuando llegaba getCoste.
  const precioListo = summary.precioOriginal != null && !valorNormal.loading;
  const mostrarTarjeta = seleccionCompleta && (precioListo || sinCuposMicro || precioNoDisponible);

  // Tracking de las dos causas (van como field_error con FieldName: NBAPI solo acepta los EventName
  // de su lista). turnos_sin_cupo se emite una vez por consulta que terminó vacía; precio_* una vez por
  // respuesta de getCoste (al volver a este paso con el precio ya cargado se vuelve a emitir
  // precio_mostrado: el análisis cuenta sesiones, no eventos).
  const prevLoadingTurnos = useRef(false);
  useEffect(() => {
    const termino = prevLoadingTurnos.current && !loadingTurnos;
    prevLoadingTurnos.current = loadingTurnos;
    if (termino && (errorTurnos !== null || dataTurnos?.length === 0)) {
      track('field_error', { fieldName: 'turnos_sin_cupo' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo cuando termina una consulta de turnos
  }, [loadingTurnos, dataTurnos, errorTurnos]);

  // Se decide con el resumen ya calculado (CheckoutPage), porque una fila con precio 0 llega como
  // respuesta "con datos" y recién el resumen la rechaza. Una sola emisión por respuesta de getCoste.
  const precioTrackeado = useRef(null);
  useEffect(() => {
    if (!precioRespondido || precioTrackeado.current === valorNormal) return;
    let fieldName = null;
    if (valorNormal.error != null) fieldName = 'precio_error';
    else if (!(valorNormal.data?.length > 0) || summary.error != null) fieldName = 'precio_sin_valor';
    else if (summary.precioOriginal != null) fieldName = 'precio_mostrado';
    if (!fieldName) return; // el resumen todavía no se calculó
    precioTrackeado.current = valorNormal;
    track(fieldName === 'precio_mostrado' ? 'field_completed' : 'field_error', { fieldName });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo cuando hay respuesta de getCoste y resumen
  }, [precioRespondido, valorNormal, summary.error, summary.precioOriginal]);

  return (
    <Container size="100%">
      <form>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {queryParams.t !== 'm' && (
            <>  
              <Row>
                <Col md={6}>
                  <Select
                    label="Fecha de Ingreso"
                    placeholder="Seleccione la fecha deseada"
                    data={fechas}
                    {...form.getInputProps("fechaIngreso")}
                    onChange={handleSelectFecha}
                    disabled={loadingFechas || fechas.length === 1}
                  />
                </Col>
                <Col md={6}>
                  <Select
                    label={typeConfig[queryParams.t].shortDescription}
                    placeholder={`Seleccione un ${typeConfig[queryParams.t].shortDescription}`}
                    searchable
                    checkIconPosition="right"
                    data={dataCarreras?.map((facultad) => ({
                      group: toTitleCase(facultad.description),
                      items: facultad.careers.map((carrera) => ({
                        value: carrera.cod,
                        // El código de plan se muestra para distinguir orientaciones de una misma carrera (ej. D6/D7/D8)
                        label: carrera.modalidad
                          ? `${toTitleCase(carrera.description)} (${carrera.cod}) · ${carrera.modalidad}`
                          : `${toTitleCase(carrera.description)} (${carrera.cod})`,
                      })),
                    }))}
                    nothingFoundMessage="No se encontraron resultados"
                    renderOption={({ option }) => {
                      const modalidad = carreraModalidadMap.get(option.value);
                      const color = MODALIDAD_COLORS[modalidad] || '#555';
                      const name = option.label.split(' · ')[0];
                      return (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '8px' }}>
                          <Text size="sm" style={{ flex: 1 }}>{name}</Text>
                          {modalidad && (
                            <Text size="xs" fw={600} style={{ color, whiteSpace: 'nowrap' }}>
                              {modalidad}
                            </Text>
                          )}
                        </div>
                      );
                    }}
                    {...form.getInputProps("carrera")}
                    onChange={(value) => handleSelectCarrera(form.values.fechaIngreso, value)}
                    disabled={
                      !!lockedCarreraFromQuery ||
                      loadingCarreras ||
                      !form.values.fechaIngreso
                    }
                  />
                </Col>
              </Row>
              <Row >
                <Col md={4}>
                  <Select
                    label="Localización"
                    placeholder="Seleccione una localización"
                    data={buildCampusOptions(dataCampus)}
                    // Sin tilde: la opcion elegida se distingue por color y negrita (ver .campus-select-* en App.css)
                    withCheckIcon={false}
                    classNames={{ option: 'campus-select-option', groupLabel: 'campus-select-group' }}
                    // Mismo render que el combo de carrera (<Text>): en el celular ese se ve bien y el <span> por defecto no
                    renderOption={({ option }) => <Text size="sm">{option.label}</Text>}
                    nothingFoundMessage="No se encontraron resultados"
                    disabled={
                      !form.values.carrera ||
                      loadingCampus ||
                      // linksEventos: la localización fijada por el link no se puede cambiar
                      (!!linkEventoData?.campusId && !!form.values.campus)
                    }
                    {...form.getInputProps("campus")}
                    onChange={handleSelectCampus}
                    //onBlur={handleSaveResponse}
                  />
                </Col>
                <Col md={4}>
                  <Select
                    label="Año"
                    placeholder="Seleccione año de cursada"
                    data={anos}
                    nothingFoundMessage="No se encontraron resultados"
                    disabled={!form.values.campus}
                    {...form.getInputProps("anioCursada")}
                    onChange={handleSelectAno}
                    //onBlur={handleSaveResponse}
                  />
                </Col>
                <Col md={4}>
                  <Select
                    label="Turno"
                    placeholder="Seleccione un turno"
                    data={dataTurnos?.map((turno) => ({
                      value: turno.c_turno_id,
                      label: turno.vc_descripcion,
                    }))}
                    nothingFoundMessage="No se encontraron resultados"
                    disabled={
                      !form.values.anioCursada ||
                      loadingTurnos ||
                      // linksEventos: el turno fijado por el link no se puede cambiar
                      (!!linkEventoData?.turnoId && !!form.values.turno)
                    }
                    {...form.getInputProps("turno")}
                    onChange={handleSelectTurno}
                    //onBlur={handleSaveResponse}
                  />
                  {sinTurnos && (
                    <Text size="xs" c="red" mt={4}>
                      No hay turnos con cupo para esta localización y año. Probá con otra localización o, si podés, con otro año.
                    </Text>
                  )}
                </Col>
              </Row>
            </>
          )}

          <div style={{transition: 'flex 0.3s ease-in-out', height:'100%'}}>
            {  (loadingTurnos || (valorNormal.loading && seleccionCompleta))  ? (
                <Grid gutter="md" style={{ minHeight: 220 }}>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Skeleton height={220} radius="md" />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Skeleton height={220} radius="md" />
                  </Grid.Col>
                </Grid>
            ) : (
              mostrarTarjeta && (
                <>
                  <Grid gutter="md" style={{ minHeight: 220 }}>
                  <Grid.Col span={{ base: 12, md: 6 }} style={{ display: 'flex', flexDirection: 'column' }}>
                      <Paper shadow="md"  radius="md" p="xl" withBorder className="tarjeta-microcredencial" >
                        <Text size="lg" fw={700} mb={12}  c="#00000">
                          {queryParams.t === 'm' ? 'Información de la Microcredencial' : 'Información de la Carrera'}
                        </Text>
                        <Stack  justify="space-around"> 
                          <div style={{flex:1}}> 
                            <Group spacing="xs" mb={10} >
                              <IconCalendar size={19} color="#f20a61" />
                              <Text size="sm" fw={500}  c="#00000" >
                                Fecha de Ingreso:
                              </Text>
                            </Group>
                            <Text size="md" fw={600} >
                              {form.values.fechaIngreso
                                ? (() => {
                                    const [year, month, day] = form.values.fechaIngreso.split('-');
                                    const fechaLocal = new Date(Number(year), Number(month) - 1, Number(day));
                                    return fechaLocal.toLocaleDateString("es-AR", {
                                      day: "2-digit",
                                      month: "long",
                                      year: "numeric",
                                    });
                                  })()
                                : "No se ha seleccionado una fecha de ingreso"}
                            </Text>
                          </div>
                          <div style={{flex:1}}> 
                            <Group spacing="xs" mb={6}>
                              <IconId size={19} color="#f20a61" />
                              <Text size="sm" fw={500} c="#00000">
                                {queryParams.t === 'm' ? 'Programa:' : 'Carrera:'}
                              </Text>
                            </Group>
                            <Text size="md"  mt={10} fw={600}>
                                {form.values.carrera ? 
                                toTitleCase(dataCarreras?.find((f) => 
                                  f.careers.find((c) => c.cod === form.values.carrera)
                                )?.careers.find((c) => c.cod === form.values.carrera)?.description)
                                : "No se ha seleccionado una carrera"}
                            </Text>
                          </div>
                        </Stack>
                      </Paper>
                    </Grid.Col>
                    
                    <Grid.Col span={{ base: 12, md: 6 }} style={{ display: 'flex', flexDirection: 'column' }}>

                      { precioNoDisponible ? (
                        <Paper shadow="md" radius="md" p="xl" withBorder bg="#f9f9f9">
                          <Text size="lg" fw={700} align="center" mb="xs">
                            No pudimos obtener el valor para esta opción.
                          </Text>
                          <Text size="md" align="center">
                            Probá con otra localización o turno, o volvé a intentarlo en unos minutos.
                          </Text>
                        </Paper>
                      ) : sinCuposMicro ? (
                        <Paper shadow="md" radius="md" p="xl" withBorder bg="#f9f9f9">
                          <Center mb="md">
                            <img src={SadFaceGif} alt="No hay cupos disponibles" style={{ maxWidth: 45, width: '100%' }} />
                          </Center>
                          <Text size="lg" fw={700}  align="center" mb="xs">
                            No quedan cupos en este momento.
                          </Text>
                          <div style={{ textAlign: 'center' }}>
                            <Text size="md" mb={8}>
                              Pero no te preocupes:
                            </Text>
                            <ul style={{fontSize:'15px' ,textAlign: 'left', display: 'inline-block', margin: 0, paddingLeft: 20 }}>
                              <li>Puede que pronto tengamos más cupos.</li>
                              <li>Hay otras opciones esperándote que también podrían ser perfectos para vos.</li>
                            </ul>
                            <Text size="md" mt={12}>
                              ¡No dejes de explorar nuestro catálogo en UAI Advance!
                            </Text>
                          </div>
                        </Paper>
                      ) : (
                        <Paper shadow="md"  radius="md" p="xl" withBorder className="tarjeta-precios" >
                          { summary.descuentoPorcentaje > 0 && summary.descuentoPorcentaje <= 100 && (
                            <Box className="badge-descuento">
                              {`${summary.descuentoPorcentaje}% OFF`}
                            </Box>
                          )}
                          <Text size="lg" fw={700} mb={12}>
                            Precios
                          </Text>

                          { areEqual(summary.precioOriginal, summary.precioDescuento) ? (
                            <>
                              <Text size="sm" c="#00000" fw={500} mb={4}>
                                {queryParams.t === 'm' ? 'Valor de la microcredencial:' : 'Valor de la matrícula:'}
                              </Text>
                              <Paper
                                shadow="sm"
                                radius="md"
                                p="md"
                                mt={12}
                                style={{ background: "#fff",  display: "inline-block",  margin: "12px 0"}}
                                className="pulse"
                              >
                                <Text className="texto-precio-final wiggle">
                                  $ {parsePriceNumber(summary.precioDescuento)}
                                </Text>
                                <Divider my="sm" />
                                <Text size="sm" mt={8}>
                                  {queryParams.t === 'm' ? 'El precio de la microcredencial corresponde a la fecha ' : 'El valor de la matrícula corresponde a la fecha '}
                                  <Text span inherit fw={700} c="rgb(208, 25, 89)">
                                    {summary.descuentoVencimiento}
                                  </Text>
                                </Text>
                              </Paper>
                            </>
                          ) : (
                            <>
                              <Text size="sm" c="dimmed" fw={500} mb={4}>
                                {queryParams.t === 'm' ? 'Valor de la microcredencial:' : 'Valor de la matrícula:'}
                              </Text>
                              <Text className="texto-precio-tachado">
                                $ {parsePriceNumber(summary.precioOriginal)}
                              </Text>

                              <Paper
                                shadow="sm"
                                radius="md"
                                p="md"
                                mt={8}
                                style={{
                                  background: "#fff",
                                  display: "inline-grid",
                                  margin: "12px 0"
                                }}
                                className="pulse"
                              >
                                <Text size="sm" c="#00000" mb={2}>
                                  Valor final con descuento:
                                </Text>
                              {(() => {
                                const showCommunity = knownUser.comunity && summary.precioComunidad != null && !areEqual(summary.precioComunidad, summary.precioDescuento);
                                return (
                                  <>
                                    <Text className={showCommunity ? 'texto-precio-tachado' : 'texto-precio-final wiggle'}>
                                      $ { parsePriceNumber(summary.precioDescuento) }
                                    </Text>
                                    { showCommunity && (
                                      <>
                                        <Text className="texto-precio-final wiggle">
                                          $ {parsePriceNumber(summary.precioComunidad)}
                                        </Text>
                                        <Text size='12px' m={0} p={0} mt={'-2px'} c={'#f20a61'} fw={500} > Descuento exclusivo comunidad UAI </Text>
                                      </>
                                    )}
                                  </>
                                );
                              })()}
                                <Divider my="sm" />
                                <Text size="sm" mt={8}>
                                  {queryParams.t === 'm' ? 'La bonificación de la microcredencial estará vigente hasta el ' : 'La bonificación de la matrícula estará vigente hasta el '}
                                  <Text span inherit fw={700} c="rgb(208, 25, 89)">
                                    {summary.descuentoVencimiento}
                                  </Text>
                                </Text>
                              </Paper>
                            </>
                          )}
                        </Paper>
                      )}
                    </Grid.Col>
                  </Grid>
                </>
              )
            )}
          </div>
        </div>
      </form>
    </Container>
  );
});

export default EducationalProgramForm;
